'use client';
import React, { useState, useRef, useEffect } from 'react';
import DashboardLayout from '@/components/DashboardLayout';
import { Search, User, Mail, Phone, MapPin, Calendar, MoreVertical, UserX, UserCheck, Trash2 } from 'lucide-react';

interface Customer {
  id: string;
  name: string;
  email: string;
  phone: string;
  address: string;
  joinDate: string;
  totalBookings: number;
  totalSpent: string;
  status: 'active' | 'inactive';
}

const initialCustomers: Customer[] = [
  { id: 'c-001', name: 'Sarah Mitchell', email: 'sarah.mitchell@email.com', phone: '+44 7700 900123', address: '12 Oak Lane, London', joinDate: 'Jan 2025', totalBookings: 8, totalSpent: '£640', status: 'active' },
  { id: 'c-002', name: 'James Thornton', email: 'james.thornton@email.com', phone: '+44 7700 900456', address: '34 Elm Street, Manchester', joinDate: 'Feb 2025', totalBookings: 5, totalSpent: '£400', status: 'active' },
  { id: 'c-003', name: 'Priya Patel', email: 'priya.patel@email.com', phone: '+44 7700 900789', address: '56 Maple Ave, Birmingham', joinDate: 'Mar 2025', totalBookings: 3, totalSpent: '£240', status: 'active' },
  { id: 'c-004', name: 'Oliver Bennett', email: 'oliver.bennett@email.com', phone: '+44 7700 900321', address: '78 Cedar Rd, Leeds', joinDate: 'Dec 2024', totalBookings: 12, totalSpent: '£960', status: 'active' },
  { id: 'c-005', name: 'Emma Clarke', email: 'emma.clarke@email.com', phone: '+44 7700 900654', address: '90 Pine Close, Bristol', joinDate: 'Nov 2024', totalBookings: 2, totalSpent: '£160', status: 'inactive' },
  { id: 'c-006', name: 'Liam Foster', email: 'liam.foster@email.com', phone: '+44 7700 900987', address: '11 Birch Way, Liverpool', joinDate: 'Apr 2025', totalBookings: 1, totalSpent: '£80', status: 'active' },
];

function CustomerRowMenu({ customer, onDeactivate, onReactivate, onDelete }: {
  customer: Customer;
  onDeactivate: () => void;
  onReactivate: () => void;
  onDelete: () => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    if (open) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(prev => !prev)}
        className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground transition-colors"
        aria-label="More actions"
      >
        <MoreVertical size={16} />
      </button>
      {open && (
        <div className="absolute right-0 top-8 z-50 w-44 bg-card border border-border rounded-xl shadow-lg py-1 overflow-hidden">
          {customer.status === 'active' ? (
            <button
              onClick={() => { onDeactivate(); setOpen(false); }}
              className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-foreground hover:bg-muted transition-colors"
            >
              <UserX size={14} className="text-warning flex-shrink-0" />
              Deactivate
            </button>
          ) : (
            <button
              onClick={() => { onReactivate(); setOpen(false); }}
              className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-foreground hover:bg-muted transition-colors"
            >
              <UserCheck size={14} className="text-success flex-shrink-0" />
              Reactivate
            </button>
          )}
          <div className="border-t border-border my-1" />
          <button
            onClick={() => { onDelete(); setOpen(false); }}
            className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-danger hover:bg-danger-bg transition-colors"
          >
            <Trash2 size={14} className="flex-shrink-0" />
            Delete Account
          </button>
        </div>
      )}
    </div>
  );
}

export default function AdminCustomersPage() {
  const [search, setSearch] = useState('');
  const [customers, setCustomers] = useState<Customer[]>(initialCustomers);
  const [deleteTarget, setDeleteTarget] = useState<Customer | null>(null);

  const filtered = customers.filter(
    (c) =>
      c.name.toLowerCase().includes(search.toLowerCase()) ||
      c.email.toLowerCase().includes(search.toLowerCase())
  );

  const handleDeactivate = (id: string) => {
    setCustomers(prev => prev.map(c => c.id === id ? { ...c, status: 'inactive' } : c));
  };

  const handleReactivate = (id: string) => {
    setCustomers(prev => prev.map(c => c.id === id ? { ...c, status: 'active' } : c));
  };

  const confirmDelete = () => {
    if (!deleteTarget) return;
    setCustomers(prev => prev.filter(c => c.id !== deleteTarget.id));
    setDeleteTarget(null);
  };

  return (
    <DashboardLayout role="admin">
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-extrabold text-foreground">Customers</h1>
            <p className="text-sm text-muted-foreground mt-0.5">{customers.length} registered customers</p>
          </div>
          <div className="relative w-full sm:w-64">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              placeholder="Search customers..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-4 py-2.5 rounded-xl border border-border bg-card text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
            />
          </div>
        </div>

        <div className="bg-card rounded-2xl border border-border card-shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-muted/40">
                  <th className="text-left px-4 py-3 font-semibold text-muted-foreground">Customer</th>
                  <th className="text-left px-4 py-3 font-semibold text-muted-foreground hidden md:table-cell">Contact</th>
                  <th className="text-left px-4 py-3 font-semibold text-muted-foreground hidden lg:table-cell">Address</th>
                  <th className="text-left px-4 py-3 font-semibold text-muted-foreground hidden sm:table-cell">Bookings</th>
                  <th className="text-left px-4 py-3 font-semibold text-muted-foreground hidden sm:table-cell">Spent</th>
                  <th className="text-left px-4 py-3 font-semibold text-muted-foreground">Status</th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {filtered.map((customer) => (
                  <tr key={customer.id} className="hover:bg-muted/30 transition-colors">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                          <User size={14} className="text-primary" />
                        </div>
                        <div>
                          <p className="font-semibold text-foreground">{customer.name}</p>
                          <p className="text-xs text-muted-foreground flex items-center gap-1">
                            <Calendar size={10} /> Joined {customer.joinDate}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 hidden md:table-cell">
                      <p className="text-foreground flex items-center gap-1.5"><Mail size={12} className="text-muted-foreground" />{customer.email}</p>
                      <p className="text-muted-foreground text-xs flex items-center gap-1.5 mt-0.5"><Phone size={10} />{customer.phone}</p>
                    </td>
                    <td className="px-4 py-3 hidden lg:table-cell">
                      <p className="text-foreground flex items-center gap-1.5"><MapPin size={12} className="text-muted-foreground flex-shrink-0" />{customer.address}</p>
                    </td>
                    <td className="px-4 py-3 hidden sm:table-cell">
                      <span className="font-semibold text-foreground">{customer.totalBookings}</span>
                    </td>
                    <td className="px-4 py-3 hidden sm:table-cell">
                      <span className="font-semibold text-foreground">{customer.totalSpent}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${customer.status === 'active' ? 'bg-success-bg text-success' : 'bg-muted text-muted-foreground'}`}>
                        {customer.status === 'active' ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <CustomerRowMenu
                        customer={customer}
                        onDeactivate={() => handleDeactivate(customer.id)}
                        onReactivate={() => handleReactivate(customer.id)}
                        onDelete={() => setDeleteTarget(customer)}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-card rounded-2xl border border-border shadow-xl w-full max-w-sm p-6">
            <div className="flex items-center justify-center w-12 h-12 rounded-full bg-danger-bg mx-auto mb-4">
              <Trash2 size={22} className="text-danger" />
            </div>
            <h2 className="text-lg font-bold text-foreground text-center mb-1">Delete Account</h2>
            <p className="text-sm text-muted-foreground text-center mb-6">
              Are you sure you want to delete <span className="font-semibold text-foreground">{deleteTarget.name}</span>? This action cannot be undone.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setDeleteTarget(null)}
                className="flex-1 py-2.5 rounded-xl border border-border text-sm font-semibold text-foreground hover:bg-muted transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                className="flex-1 py-2.5 rounded-xl bg-danger text-white text-sm font-semibold hover:opacity-90 active:scale-95 transition-all duration-150"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}
